"use client";

import { ArrowLeft, ArrowRight } from "iconsax-reactjs";
import { Button } from "@/components/ui/button";
import { useGoBack } from "@/hooks/useGoBack";
import { cn } from "@/lib/utils";

interface BackButtonHeaderProps {
  title: string;
  onClick?: () => void;
  href?: string;
  className?: string;
  showBorder?: boolean;
  borderColor?: "subtle" | "light";
  titleInsideButton?: boolean;
  /**
   * Boxed treatment (Figma 1215:53608): the title fills the row and the back
   * arrow sits in a tinted square pinned to the far end. Opt-in — the 20+
   * existing call sites keep the plain inline arrow.
   */
  boxed?: boolean;
}

export const BackButtonHeader = ({
  title,
  onClick,
  href,
  className,
  showBorder = false,
  borderColor = "subtle",
  titleInsideButton = false,
  boxed = false,
}: BackButtonHeaderProps) => {
  // `href` is the FALLBACK destination (used only when there's no history to
  // pop, e.g. a direct visit) — not the primary action. Clicking always tries
  // to return the user to the real previous page first.
  const goBack = useGoBack(href);
  const handleClick = onClick ?? goBack;

  const borderClass = showBorder
    ? borderColor === "light"
      ? "border-b-3 border-stroke-light"
      : "border-b-3 border-stroke-subtle"
    : "";

  if (titleInsideButton) {
    return (
      <div className={cn("w-full", borderClass, className)}>
        <Button
          className="p-0"
          variant="light"
          startIcon={<ArrowRight size={20} />}
          onClick={handleClick}
        >
          {title}
        </Button>
      </div>
    );
  }

  if (boxed) {
    return (
      <div className={cn("flex items-center gap-2.5", borderClass, className)}>
        <h3 className="min-w-0 flex-1 truncate text-lg font-semibold text-text-primary">
          {title}
        </h3>
        <Button
          variant="solid"
          colors="default"
          size="icon"
          radius="xs"
          aria-label={title}
          onClick={handleClick}
          className="size-9 flex-none bg-bg-tertiary/60"
        >
          <ArrowLeft size={16} />
        </Button>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", borderClass, className)}>
      <Button className="p-0" variant="light" size="icon" onClick={handleClick}>
        <ArrowRight size={20} />
      </Button>
      <h3 className="text-base font-semibold">{title}</h3>
    </div>
  );
};
